import { IconButton, Menu, MenuItem, makeStyles } from "@material-ui/core";
import DarkDataGrid from "common/components/DarkDataGrid/DarkDataGrid";
import { SetLoading } from "common/components/Loader/Loader";
import DeleteConfirmModal from "common/components/Modals/DeleteConfirmModal/DeleteConfirmModal";
import RecordInUseModal from "common/components/Modals/RecordInUseModal/RecordInUseModal";
import { connect } from "common/connect";
import EventFormatHelper from "common/helpers/eventFormatHelper";
import ModalHelper from "common/helpers/modalHelper";
import Store from "common/store";
import StoreKeys from "common/storeKeys";
// eslint-disable-next-line no-unused-vars
import { EventFormat } from "common/types/EventFormat";
import * as React from "react";
import { useEffect } from "react";
import EventFormatService from "services/EventFormatService";
import { Colors } from "styles/Colors";
import { EditOutlined, MoreVert, FileCopy, Delete } from "@material-ui/icons";
import { PanZoomHelper } from "common/helpers/panZoomHelper";

const propKeys = {
	eventFormats: StoreKeys.EVENTFORMATBUILDER.EVENT_FORMATS,
};

const classStyles = makeStyles(() => {
	return {
		gridFlex: {
			flex: 1,
			minHeight: 0,
		},
		actionButton: {
			color: Colors.primary,
		},
		kebabButton: {
			color: Colors.primary,
			marginLeft: 12,
		},
		menuPaper: {
			border: `1px solid ${Colors.panelLight}`,
		},
	};
});

const dateFormatter = (object) =>
	object.value ? object.value.split("T")[0] : "";

// Sort by usage date
const lastUsedComparator = (a, b) => {
	if (!a && !b) return 0;
	if (!a) return -1;
	if (!b) return 1;
	return new Date(a) - new Date(b);
};

export function editEventFormat(format) {
	PanZoomHelper.resetView(0, 0, 0.75);
	const formatBuilder = EventFormatHelper.getEventFormatBuilder(format);
	Store.set(StoreKeys.EVENTFORMATBUILDER.CURRENT_FORMAT, formatBuilder);
}

function cloneEventFormat(format) {
	PanZoomHelper.resetView(0, 0, 0.75);

	/** @type {EventFormat} */
	const newFormat = JSON.parse(JSON.stringify(format));
	// Reset id so it's handled as a new format when saved.
	newFormat.id = 0;

	const formatBuilder = EventFormatHelper.getEventFormatBuilder(newFormat);
	Store.set(StoreKeys.EVENTFORMATBUILDER.CURRENT_FORMAT, formatBuilder);
}

function deleteEventFormat(format) {
	// Used by an event? The API blocks the delete, so flag it up front instead of round-tripping.
	if (format.recentEventNames && format.recentEventNames.length) {
		ModalHelper.openModal(<RecordInUseModal objectName="Event Format" />);
		return;
	}

	ModalHelper.openModal(
		<DeleteConfirmModal
			objectName="Event Format"
			onConfirm={async () => {
				SetLoading(true, "Deleting Format");
				try {
					await EventFormatService.deleteFormat(format.id);
					const formats = Store.get(
						StoreKeys.EVENTFORMATBUILDER.EVENT_FORMATS
					);
					Store.set(
						StoreKeys.EVENTFORMATBUILDER.EVENT_FORMATS,
						formats.filter((ef) => ef.id !== format.id)
					);
					SetLoading(false);
				} catch (error) {
					SetLoading(false);
					ModalHelper.openModal(
						<RecordInUseModal objectName="Event Format" />
					);
				}
			}}
		/>
	);
}

/**
 * Per-row Edit button plus a three-dot menu for Clone / Delete.
 *
 * @param {{ format: EventFormat }} props
 */
function RowActions({ format }) {
	const [anchorEl, setAnchorEl] = React.useState(null);
	const classNames = classStyles();

	const openMenu = (event) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};
	const closeMenu = () => setAnchorEl(null);

	return (
		<>
			<IconButton
				size="small"
				aria-label="edit"
				className={classNames.actionButton}
				onClick={(event) => {
					event.stopPropagation();
					editEventFormat(format);
				}}
			>
				<EditOutlined fontSize="small" />
			</IconButton>
			<IconButton
				size="small"
				aria-label="more"
				className={classNames.kebabButton}
				onClick={openMenu}
			>
				<MoreVert fontSize="small" />
			</IconButton>
			<Menu
				classes={{ paper: classNames.menuPaper }}
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={closeMenu}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				getContentAnchorEl={null}
			>
				<MenuItem
					onClick={() => {
						closeMenu();
						cloneEventFormat(format);
					}}
				>
					<FileCopy fontSize="small" />
					&nbsp; Clone
				</MenuItem>
				<MenuItem
					onClick={() => {
						closeMenu();
						deleteEventFormat(format);
					}}
				>
					<Delete fontSize="small" />
					&nbsp; Delete
				</MenuItem>
			</Menu>
		</>
	);
}

const columns = [
	{ field: "id", headerName: "ID", flex: 0.2 },
	{ field: "name", headerName: "Name", flex: 1 },
	{
		field: "lastUsed",
		headerName: "Recent Events",
		flex: 1.5,
		sortComparator: lastUsedComparator,
		renderCell: (params) => (params.row.recentEventNames || []).join(", "),
	},
	{
		field: "created",
		headerName: "Date Created",
		width: 140,
		valueFormatter: dateFormatter,
	},
	{
		field: "modified",
		headerName: "Date Modified",
		width: 140,
		valueFormatter: dateFormatter,
	},
	{
		field: "actions",
		headerName: "",
		width: 110,
		sortable: false,
		filterable: false,
		disableColumnMenu: true,
		renderCell: (params) => <RowActions format={params.row} />,
	},
];

/**
 * @typedef ComponentProps
 * @type {object}
 * @property {EventFormat} currentFormat
 * @property {Array<EventFormat>} eventFormats
 */

/**
 * @param {ComponentProps} props
 */
export function EventFormatSelectorC(props) {
	const { eventFormats } = props;
	const classNames = classStyles();

	useEffect(() => {
		if (!eventFormats) {
			EventFormatService.getAllFormats().then((data) => {
				Store.set(StoreKeys.EVENTFORMATBUILDER.EVENT_FORMATS, data);
			});
		}
	});

	return (
		<div
			style={{
				flex: 1,
				minHeight: 0,
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<DarkDataGrid
				rows={eventFormats || []}
				columns={columns}
				pageSize={25}
				rowsPerPageOptions={[25, 50, 100]}
				disableSelectionOnClick
				className={classNames.gridFlex}
			/>
		</div>
	);
}

function EventFormatSelector(props) {
	return connect(<EventFormatSelectorC />, propKeys, props);
}

export default EventFormatSelector;
